// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import {
  BaseMetricConfig,
  BaseMetricEntity,
  ChartType,
  ScoreLevel,
} from './base-metric.entity';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse,
} from './metric-chart-base.entity';
import { formatDataSources } from './metric-column-formatters.utility';

/**
 * Configuration interface for AverageTimeToMergePrs entity
 */
type AverageTimeToMergePrsConfig = BaseMetricConfig;

/**
 * Average time to merge PRs data model from SQL query (API returns uppercase keys)
 */
export interface AvgTimeToMergePrsData {
  REPOSITORY_URL: string;
  PLATFORM: string;
  GITHUB_USERNAME: string;
  USER_PHOTO_URL: string;
  PULL_REQUEST_URL: string;
  PULL_REQUEST_TITLE: string;
  MERGED_AT: string;
  MERGE_TIME: number;
}

/**
 * Average time to merge PRs summary data model from SQL query
 */
export interface AvgTimeToMergePrsSummaryData {
  mergeTimeAvg: number;
  dataSources: string;
}

/**
 * AverageTimeToMergePrs - Unified entity for average time to merge PRs
 *
 * This entity encapsulates both:
 * 1. Business logic for determining average time to merge PRs influence levels and display logic
 * 2. Data access layer for fetching and transforming PR merge time data
 *
 * Business Rules:
 * - >10% slower average PR merge time = Silent (0 points)
 * - 10% - 0.1% slower than the average PR merge time = Participating (1 point)
 * - 0 - 25% faster than the average PR merge time = Contributing (2 points)
 * - >25% faster than the average PR merge time = Leading (3 points)
 */
@Injectable({
  providedIn: 'root',
})
export class AverageTimeToMergePrs extends BaseMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  static readonly METRIC_TYPE = 'avg_time_to_merge_prs' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: AverageTimeToMergePrsConfig = {
    metricType: 'avg_time_to_merge_prs',
    display: {
      displayName: 'Average Time to Merge PRs',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'Does this company have a faster or slower PR merge time compared to the average?',
        scoringRules: [
          'No data / Null: = 0 points',
          '>10% slower than the average PR merge time = 0 points',
          '-10% slower to +20% faster than the average PR merge time = 1 points',
          '20-40% faster than the average PR merge time = 2 points',
          '>40% faster than the average PR merge time = 3 points',
          '0 points = <b>Silent</b>',
          '1 points = <b>Participating</b>',
          '2 points = <b>Contributing</b>',
          '3 points = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: 'No PR merge time data available for our company employees.',
      },
      positiveValue: {
        descriptionTemplate: 'PRs from our company\'s employees are merged {percentage}% {comparison} than average.',
      }
    }
  };

  static getMetricConfig() {
    return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for average time to merge PRs
   * Uses backend-provided percentage, speed data, and level
   *
   * Handles 4 scenarios (CD-3267):
   * 1. Faster: Show "{percentage}% faster"
   * 2. Slower: Show "{percentage}% slower"
   * 3. Same: Show "at average speed"
   * 4. No Data (NULL): Show "No PR merge time data available"
   */
  static getBottomSummary(value?: { percent: string | null; speed: 'faster' | 'slower' | 'same' | null } | string | number, level?: ScoreLevel): MetricBottomSummary {
    // Handle different input formats - backend provides { percent: string | null, speed: 'faster' | 'slower' | 'same' | null }
    let percentage: string | null = null;
    let speed: 'faster' | 'slower' | 'same' | null = null;

    if (typeof value === 'object' && value !== null && 'percent' in value) {
      percentage = value.percent;
      speed = value.speed;
    }

    const bottomSummary = this.CONFIG.bottomSummary;

    // Scenario 4: No Data (NULL values from backend)
    // When percentage or speed is null, it means no merged PRs data
    if (percentage === null || speed === null) {
      const styles = level ? this.getStylesByLevel(level) : this.getNullValueStyles();

      return {
        title: this.CONFIG.display.displayName,
        description: bottomSummary.zeroValue.description,
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    // Scenario 3: At Average (same)
    if (speed === 'same') {
      const styles = level ? this.getStylesByLevel(level) : this.getNullValueStyles();

      return {
        title: this.CONFIG.display.displayName,
        description: 'PRs from our company\'s employees are merged at the average speed.',
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    // Scenario 1 & 2: Faster or Slower
    const description = this.formatDescription(
      bottomSummary.positiveValue.descriptionTemplate,
      {
        percentage: percentage,
        comparison: speed
      }
    );

    const styles = level ? this.getStylesByLevel(level) : this.getNullValueStyles();

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }

  // ============================================================================
  // Data Access Layer - Instance Methods for Chart/Table Data
  // ============================================================================

  /**
   * Get columns for avg time to merge PRs table
   */
  getColumns(): Column[] {
    return [
      { name: 'Repo', prop: 'repositoryUrl', },
      { name: 'Our Contributors', prop: 'contributor', },
      { name: 'PR Name', prop: 'prName' },
      { name: 'Date', prop: 'mergedAt' },
      { name: 'Merge Time', prop: 'mergeTime' },
    ];
  }

  /**
   * Get API endpoint for avg time to merge PRs
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/avg-time-to-merge-prs`;
  }

  /**
   * Fetch avg time to merge PRs data from backend
   * Executes SQL to get pull requests with merge times
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '')
          .set('timeRangeName', params.timeRangeName || 'last_24mo');

        return this.http
          .get<
            MetricChartApiResponse<AvgTimeToMergePrsData>
          >(this.getEndpoint(), { params: httpParams })
          .pipe(
            map(response => {
              if (!response.success) {
                throw new Error(
                  response.message || 'Failed to fetch avg time to merge PRs data'
                );
              }

              return response.data.map(pr =>
                this.transformToDataRow(pr)
              );
            }),
            catchError(error => {
              console.error('Error fetching avg time to merge PRs data:', error);
              return of([]);
            })
          );
      }),
      catchError(error => {
        console.error('Error in avg time to merge PRs entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform PR data to DataRow format for the table
   */
  private transformToDataRow(pr: AvgTimeToMergePrsData): DataRow {
    const platformValue = this.formatPlatformValue(pr.PLATFORM, pr.GITHUB_USERNAME);

    return {
      repositoryUrl: {
        text: this.extractRepositoryName(pr.REPOSITORY_URL),
        // clickableText: true,
        // extraData: {
        //   url: pr.REPOSITORY_URL,
        //   fullUrl: pr.REPOSITORY_URL
        // }
      },
      contributor: platformValue,
      prName: this.formatPRLink(pr.PULL_REQUEST_URL, pr.PULL_REQUEST_TITLE),
      mergedAt: { text: this.formatDate(pr.MERGED_AT) },
      mergeTime: { text: this.formatMergeTime(pr.MERGE_TIME) },
    };
  }

  /**
   * Format platform value with appropriate prefix and icons
   */
  private formatPlatformValue(platform: string, platformValue: string): { text: string; isHtml?: boolean } {
    if (platform.toLowerCase() === 'github') {
      return {
        text: `<i class="fab fa-github" style="margin-right: 4px;"></i> &nbsp;@${platformValue}`,
        isHtml: true
      };
    }

    return { text: platformValue };
  }

  /**
   * Extract repository name from URL
   */
  private extractRepositoryName(url: string): string {
    try {
      const parts = url.split('/');
      return parts.length >= 2 ? `${parts[parts.length - 2]}/${parts[parts.length - 1]}` : url;
    } catch {
      return url;
    }
  }

  /**
   * Format merge time in days
   */
  private formatMergeTime(days: number): string {
    if (days < 1) {
      return '< 1 day';
    } else if (days === 1) {
      return '1 day';
    } else {
      return `${Math.round(days)} days`;
    }
  }

  /**
   * Fetch avg time to merge PRs summary data from backend
   * Executes SQL: select ROUND(avg(merged_in_seconds)/ 86400.0, 2) as merge_time_avg,
   * listagg(distinct platform) data_sources
   * where is_pull_request_last_24mo and project_slug='{project_slug}' and merged_at is not null
   */
  getSummaryData(
    params: MetricChartData
  ): Observable<{ columns: Column[]; data: DataRow[] }> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '')
          .set('timeRangeName', params.timeRangeName || 'last_24mo');

        return this.http
          .get<{
            success: boolean;
            message?: string;
            data: AvgTimeToMergePrsSummaryData;
          }>(`${this.getEndpoint()}/summary`, { params: httpParams })
          .pipe(
            map(response => {
              if (!response.success) {
                throw new Error(
                  response.message || 'Failed to fetch avg time to merge PRs summary'
                );
              }

              // Define columns for summary table
              const summaryColumns: Column[] = [
                { name: 'Definition', prop: 'definition' },
                { name: 'Average for this project', prop: 'averageTime' },
                { name: 'Data sources', prop: 'dataSource' },
              ];

              // Transform API response to summary table format
              const summaryData: DataRow[] = [
                {
                  definition: {
                    text: 'How responsive were other project members in merging PRs. View all contribution definitions in <a href="https://insights.linuxfoundation.org/docs/introduction/contributions/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.',
                    isHtml: true
                  },
                  averageTime: {
                    text: response.data.mergeTimeAvg ?
                      this.formatMergeTime(response.data.mergeTimeAvg) :
                      'N/A',
                  },
                  dataSource: { text: formatDataSources(response.data.dataSources || 'N/A') },
                },
              ];

              return { columns: summaryColumns, data: summaryData };
            }),
            catchError(error => {
              console.error('Error fetching avg time to merge PRs summary:', error);
              // Return fallback data on error
              const fallbackColumns: Column[] = [
                { name: 'Definition', prop: 'definition' },
                { name: 'Average for this project', prop: 'averageTime' },
                { name: 'Data sources', prop: 'dataSource' },
              ];
              const fallbackData: DataRow[] = [
                {
                  definition: {
                    text: 'How responsive were other project members in merging PRs. View all contribution definitions in <a href="https://insights.linuxfoundation.org/docs/introduction/contributions/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.',
                    isHtml: true
                  },
                  averageTime: { text: 'N/A' },
                  dataSource: { text: 'N/A' },
                },
              ];
              return of({ columns: fallbackColumns, data: fallbackData });
            })
          );
      }),
      catchError(error => {
        console.error('Error in avg time to merge PRs summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = [
          { name: 'Definition', prop: 'definition' },
          { name: 'Average for this project', prop: 'averageTime' },
          { name: 'Data sources', prop: 'dataSource' },
        ];
        const fallbackData: DataRow[] = [
          {
            definition: {
              text: 'How responsive were other project members in merging PRs. View all contribution definitions in <a href="https://insights.linuxfoundation.org/docs/introduction/contributions/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.',
              isHtml: true
            },
            averageTime: { text: 'N/A' },
            dataSource: { text: 'N/A' },
          },
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }

  /**
   * Format PR link with truncated title
   * If title is empty or missing, show the PR URL instead
   */
  private formatPRLink(
    prUrl: string,
    prTitle: string
  ): { text: string; isHtml: boolean } {
    const maxLength = 60;

    // If title is empty or missing, use the PR URL as display text
    const displayText = prTitle && prTitle.trim()
      ? (prTitle.length > maxLength ? `${prTitle.substring(0, maxLength)}...` : prTitle)
      : prUrl;

    const titleAttr = prTitle && prTitle.trim() ? prTitle : prUrl;

    return {
      text: `<a href="${prUrl}" target="_blank" rel="noopener noreferrer" title="${titleAttr}">${displayText}</a>`,
      isHtml: true
    };
  }
}

